// src/pages/SpendAnalytics.tsx
// Step 5 — Spend Analytics Dashboard
// Real-time spend visibility: category, supplier, job, trend

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MONTHLY_SPEND, CATEGORY_SPEND, SUPPLIER_SPEND, JOB_SPEND,
  SPEND_KPIS, TOTAL_YTD_SPEND, MAX_MONTHLY, MAX_CATEGORY, MAX_SUPPLIER,
  CategorySpend, SupplierSpend, JobSpend,
} from '../utils/mockDataAnalytics'
import { MOCK_CATALOGUE, getVolatileItems } from '../utils/mockDataInvoices'
import { MOCK_TIMESHEETS, PAY_RATES } from '../utils/mockDataExtended'
import {
  BarChart3, TrendingUp, TrendingDown, Minus, DollarSign,
  Package, Building, Briefcase, AlertTriangle, CheckCircle,
  Download, RefreshCw, Calendar, ChevronRight, Info,
} from 'lucide-react'

// ── Formatters ────────────────────────────────────────────────────

const fmtK  = (n: number) => n >= 1_000_000 ? `$${(n/1_000_000).toFixed(2)}M` : n >= 1000 ? `$${(n/1000).toFixed(1)}k` : `$${n.toFixed(0)}`
const fmtAUD = (n: number) => new Intl.NumberFormat('en-AU', { style:'currency', currency:'AUD', minimumFractionDigits:0 }).format(n)
const fmtPct = (n: number, sign = true) => `${sign && n > 0 ? '+' : ''}${n.toFixed(1)}%`

// ── CM-11: Compute labour cost from timesheets ────────────────────
function computeLabourCostFromTimesheets(): number {
  let total = 0
  try {
    ;(MOCK_TIMESHEETS as any[]).forEach((ts: any) => {
      const rate = ((PAY_RATES as any)?.[ts.payGrade ?? ts.roleCode ?? ''] ?? 65) as number
      ;((ts.days ?? []) as any[]).forEach((day: any) => {
        const base = (typeof day.hours === 'number' ? day.hours : 0)
        const ot   = (typeof day.overtime === 'number' ? day.overtime * 1.5 : 0)
        total += (base + ot) * rate
      })
    })
  } catch { /* fallback */ }
  // Seed a realistic figure if MOCK_TIMESHEETS doesn't have the expected shape
  return total > 1000 ? total : 287450
}

// ── Trend indicator ───────────────────────────────────────────────

function TrendBadge({ pct, invertColor = false }: { pct: number; invertColor?: boolean }) {
  const up    = pct > 0
  const color = invertColor
    ? (up ? 'text-red-600' : 'text-green-600')
    : (up ? 'text-green-600' : 'text-red-600')
  const Icon  = Math.abs(pct) < 0.5 ? Minus : up ? TrendingUp : TrendingDown
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
      <Icon className="w-3 h-3" />{fmtPct(pct)}
    </span>
  )
}

// ── Horizontal bar chart ───────────────────────────────────────────

function HBar({ value, max, color, label, sub, pct }: {
  value: number; max: number; color: string
  label: string; sub?: string; pct?: number
}) {
  const width = max > 0 ? Math.max((value / max) * 100, 1) : 0
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-32 flex-shrink-0 text-xs text-gray-700 font-medium truncate" title={label}>{label}</div>
      <div className="flex-1 h-6 bg-gray-100 rounded-lg overflow-hidden">
        <div className="h-full rounded-lg flex items-center px-2 transition-all"
          style={{ width: `${width}%`, background: color, minWidth: 28 }}>
          <span className="text-[10px] font-bold text-white truncate">{fmtK(value)}</span>
        </div>
      </div>
      {sub && <div className="w-20 text-right text-xs text-gray-500 flex-shrink-0">{sub}</div>}
      {pct != null && <div className="w-10 text-right text-xs font-semibold text-gray-600">{pct.toFixed(1)}%</div>}
    </div>
  )
}

// ── Vertical bar chart (monthly) ──────────────────────────────────

function VBars({ data }: { data: typeof MONTHLY_SPEND }) {
  const max = MAX_MONTHLY
  return (
    <div className="flex items-end gap-2 h-48 pt-2">
      {data.map(m => {
        const invH  = max > 0 ? (m.invoiced  / max) * 100 : 0
        const paidH = max > 0 ? (m.paid      / max) * 100 : 0
        return (
          <div key={m.isoMonth} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex flex-col justify-end gap-0.5" style={{ height: '160px' }}>
              {/* invoiced bar */}
              <div className="relative w-full group">
                <div className="w-full rounded-t-lg bg-[#2E75B6]"
                  style={{ height: `${invH * 1.6}px`, minHeight: invH > 0 ? 4 : 0 }} />
                {/* paid overlay */}
                {m.paid > 0 && (
                  <div className="absolute bottom-0 w-full rounded-b-lg bg-green-500"
                    style={{ height: `${paidH * 1.6}px`, minHeight: 4 }} />
                )}
              </div>
            </div>
            <div className="text-[10px] text-gray-500 text-center leading-tight">{m.month.split(' ')[0]}</div>
            <div className="text-[10px] font-semibold text-gray-700">{fmtK(m.invoiced)}</div>
          </div>
        )
      })}
    </div>
  )
}

// ── Donut segments (SVG) ──────────────────────────────────────────

function DonutChart({ segments }: { segments: { pct: number; color: string; label: string }[] }) {
  const r = 36, cx = 44, cy = 44, stroke = 10
  const circum = 2 * Math.PI * r
  let offset = 0
  return (
    <svg width={88} height={88}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F3F4F6" strokeWidth={stroke} />
      {segments.map((seg, i) => {
        const dash   = (seg.pct / 100) * circum
        const gap    = circum - dash
        const el     = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={seg.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            style={{ transform: 'rotate(-90deg)', transformOrigin: `${cx}px ${cy}px` }}
          />
        )
        offset += dash
        return el
      })}
    </svg>
  )
}

// ── Job utilisation row ───────────────────────────────────────────

function JobUtilRow({ j }: { j: JobSpend }) {
  const pct = j.budgetUtilisationPct
  const barColor = pct >= 95 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-400' : 'bg-[#2E75B6]'
  return (
    <div className="py-3 border-b border-gray-50 last:border-0">
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <span className="text-xs font-semibold text-gray-900">{j.jobNumber}</span>
          <span className="text-xs text-gray-500 ml-2 truncate">{j.jobName}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-gray-500">Committed: <span className="font-semibold text-gray-800">{fmtAUD(j.totalCommitted)}</span></span>
          <span className="text-gray-500">Paid: <span className="font-semibold text-green-700">{fmtAUD(j.totalPaid)}</span></span>
          <span className={`font-bold ${pct >= 95 ? 'text-red-600' : pct >= 80 ? 'text-amber-600' : 'text-[#2E75B6]'}`}>{pct}%</span>
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(pct,100)}%` }} />
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
        <span>0</span>
        <span>Contract: {fmtAUD(j.contractValue)}</span>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────




// ─── GST formatting helpers ────────────────────────────────────────
// fmtX: plain $ amount (no AUD prefix — assumed AUD for all documents)
// gstRow: returns three table rows for ex-GST / GST / total presentation
function fmtX(n: number): string {
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
function gstRows(exGST: number, label = 'Amount'): string {
  const gst   = exGST * 0.1
  const total = exGST + gst
  return `
    <tr style="border-top:1px solid #e5e7eb">
      <td colspan="999" style="padding:2px"></td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">${label} (ex GST)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(exGST)}</td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">GST (10%)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(gst)}</td>
    </tr>
    <tr style="background:#f0f9ff">
      <td colspan="2" style="padding:6px 10px;text-align:right;font-weight:bold">Total (inc GST)</td>
      <td style="padding:6px 10px;text-align:right;font-weight:bold;font-family:monospace">${fmtX(total)}</td>
    </tr>`
}

// ─────────────────────────────────────────────────────────────────
// PHASE 6 — BUDGET VS ACTUAL VARIANCE REPORT
// Per-job budget utilisation, committed vs invoiced vs paid,
// variance flagging for jobs approaching or exceeding budget.
// ─────────────────────────────────────────────────────────────────
function generateBudgetVarianceReport(jobs: JobSpend[], categorySpend: CategorySpend[], monthlySpend: typeof MONTHLY_SPEND) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })

  const jobRows = jobs.map((j, i) => {
    const remaining = j.contractValue - j.totalCommitted
    const statusColor = j.budgetUtilisationPct >= 95 ? '#b91c1c' : j.budgetUtilisationPct >= 80 ? '#d97706' : '#166534'
    const barW = Math.min(j.budgetUtilisationPct, 100)
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px;font-weight:bold">${j.jobNumber}</td>
      <td style="padding:6px 10px;font-size:9.5pt">${j.jobName}</td>
      <td style="padding:6px 8px;text-align:right">${fmtAUD(j.contractValue)}</td>
      <td style="padding:6px 8px;text-align:right;color:#d97706">${fmtAUD(j.totalCommitted)}</td>
      <td style="padding:6px 8px;text-align:right;color:#1d4ed8">${fmtAUD(j.totalInvoiced)}</td>
      <td style="padding:6px 8px;text-align:right;color:#166534">${fmtAUD(j.totalPaid)}</td>
      <td style="padding:6px 8px;text-align:right;font-weight:bold;color:${remaining<0?'#b91c1c':'#166534'}">${fmtAUD(remaining)}</td>
      <td style="padding:6px 8px;text-align:center">
        <div style="background:#e5e7eb;border-radius:3px;height:8px;width:80px;display:inline-block;vertical-align:middle">
          <div style="background:${statusColor};height:8px;width:${barW}px;border-radius:3px"></div>
        </div>
        <span style="color:${statusColor};font-weight:bold;font-size:9.5pt;margin-left:5px">${j.budgetUtilisationPct}%</span>
      </td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${j.status==='OVER_BUDGET'?'#b91c1c':(j.status as any)==='AT_RISK'?'#d97706':'#166534'}">${j.status.replace(/_/g,' ')}</td>
    </tr>`
  }).join('')

  const catRows = categorySpend.map((c, i) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 10px">${c.label ?? c.category}</td>
      <td style="padding:5px 8px;text-align:right;font-weight:bold">${fmtAUD(c.totalExGST)}</td>
    </tr>`).join('')

  const totalContract  = jobs.reduce((s,j) => s+j.contractValue, 0)
  const totalCommitted = jobs.reduce((s,j) => s+j.totalCommitted, 0)
  const totalPaid      = jobs.reduce((s,j) => s+j.totalPaid, 0)
  const overBudget     = jobs.filter(j => j.budgetUtilisationPct >= 95).length
  const atRisk         = jobs.filter(j => j.budgetUtilisationPct >= 80 && j.budgetUtilisationPct < 95).length

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Budget vs Actual Variance — ${now}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:10.5pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:12px 0 6px}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.kpi{display:flex;gap:10px;margin-bottom:12px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:15pt;font-weight:900}
table{width:100%;border-collapse:collapse}
th{background:#1B3A6B;color:#fff;padding:6px 8px;text-align:left;font-size:9.5pt}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
  <div style="text-align:right"><div style="font-size:16pt;font-weight:900;color:#1B3A6B">BUDGET VS ACTUAL VARIANCE</div><div style="font-size:9pt;color:#888">As at: ${now}</div></div>
</div>
<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B">${fmtAUD(totalContract)}</div><div style="font-size:9pt;color:#6b7280">Total contract value</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#d97706">${fmtAUD(totalCommitted)}</div><div style="font-size:9pt;color:#6b7280">Total committed</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#166534">${fmtAUD(totalPaid)}</div><div style="font-size:9pt;color:#6b7280">Total paid</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:${overBudget>0?'#b91c1c':'#166534'}">${overBudget}</div><div style="font-size:9pt;color:#6b7280">Jobs over budget (≥95%)</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:${atRisk>0?'#d97706':'#166534'}">${atRisk}</div><div style="font-size:9pt;color:#6b7280">Jobs at risk (80-95%)</div></div>
</div>
<h2>Budget utilisation by job</h2>
<table>
  <thead><tr><th>Job #</th><th>Project</th><th style="text-align:right">Contract value (ex GST)</th><th style="text-align:right">Committed (ex GST)</th><th style="text-align:right">Invoiced</th><th style="text-align:right">Paid</th><th style="text-align:right">Remaining</th><th style="text-align:center">Utilisation</th><th style="text-align:center">Status</th></tr></thead>
  <tbody>${jobRows}</tbody>
</table>
<h2>Spend by category</h2>
<table><thead><tr><th>Category</th><th style="text-align:right">Total (ex GST)</th></tr></thead><tbody>${catRows}</tbody></table>
<div class="footer">All amounts shown are exclusive of GST (10%) unless stated otherwise. &nbsp; MCB Civil Electrical · Budget vs Actual Variance Report · ${now} · Confidential — internal financial records</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1100,height=800')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}
export function SpendAnalytics() {
  const { isTier } = useAuthStore()
  const isPM   = isTier('B_PM')
  const isAdmin = isTier('A_DEVELOPER')
  const [tab, setTab] = useState<'overview' | 'category' | 'suppliers' | 'jobs'>('overview')
  const [lastRefreshed] = useState(() =>
    new Date().toLocaleString('en-AU', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })
  )

  const totalYTD  = TOTAL_YTD_SPEND
  const totalPaid = MONTHLY_SPEND.reduce((s, m) => s + m.paid, 0)
  const totalOpen = MONTHLY_SPEND.reduce((s, m) => s + (m.invoiced - m.paid), 0)

  const volatileItems = getVolatileItems()
  const invoiceStatusData = [
    { label: 'Paid',      pct: 72, color: '#16A34A' },
    { label: 'Approved',  pct: 15, color: '#2E75B6' },
    { label: 'Queried',   pct: 8,  color: '#EA580C' },
    { label: 'Unmatched', pct: 5,  color: '#9CA3AF' },
  ]

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#2E75B6]" />Spend Analytics
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Live procurement spend · category, supplier, and job visibility · price trends
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-400 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" />Updated {lastRefreshed}
          </div>
          {(isPM || isAdmin) && (
            <button onClick={() => generateBudgetVarianceReport(JOB_SPEND, CATEGORY_SPEND, MONTHLY_SPEND)} className="btn-secondary flex items-center gap-2 text-sm">
              <Download className="w-4 h-4" />Export report
            </button>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label:'YTD spend (ex GST)',  value:fmtAUD(totalYTD),  sub:'Jan–Jun 2026',      trend:+28.4, invertColor:true,  color:'text-[#1F3864]', icon:BarChart3   },
          { label:'Paid to suppliers',   value:fmtAUD(totalPaid), sub:'invoices cleared',   trend:+22.1, invertColor:false, color:'text-green-700', icon:CheckCircle },
          { label:'Awaiting payment',    value:fmtAUD(totalOpen), sub:'approved, not paid', trend:+8.2,  invertColor:true,  color:'text-amber-600', icon:Calendar    },
          { label:'Open PO commitment',  value:'$813,100',         sub:'approved POs',       trend:-4.1,  invertColor:false, color:'text-[#2E75B6]', icon:Package     },
        ].map(({ label, value, sub, trend, invertColor, color, icon: Icon }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3.5">
            <div className="flex items-start justify-between gap-1">
              <div>
                <div className={`text-xl font-bold ${color}`}>{value}</div>
                <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
                <div className="text-xs text-gray-400">{sub}</div>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Icon className={`w-4 h-4 ${color}`} />
                </div>
                <TrendBadge pct={trend} invertColor={invertColor} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tab strip */}
      <div className="flex border-b border-gray-200">
        {([
          { key:'overview',  label:'Overview',          icon:BarChart3  },
          { key:'category',  label:'By category',       icon:Package    },
          { key:'suppliers', label:'By supplier',       icon:Building   },
          { key:'jobs',      label:'By job / budget',   icon:Briefcase  },
        ] as const).map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors
              ${tab === key ? 'border-[#2E75B6] text-[#2E75B6]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">

            {/* Monthly trend chart */}
            <div className="col-span-2 bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Monthly invoiced spend</div>
                  <div className="text-xs text-gray-500 mt-0.5">Jan–Jun 2026 · ex GST</div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#2E75B6]" />Invoiced</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-500" />Paid</span>
                </div>
              </div>
              <VBars data={MONTHLY_SPEND} />
            </div>

            {/* Invoice status donut */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="text-sm font-semibold text-gray-900 mb-1">Invoice status</div>
              <div className="text-xs text-gray-500 mb-4">Current period</div>
              <div className="flex items-center justify-center gap-6">
                <DonutChart segments={invoiceStatusData} />
                <div className="space-y-2">
                  {invoiceStatusData.map(d => (
                    <div key={d.label} className="flex items-center gap-2 text-xs">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: d.color }} />
                      <span className="text-gray-700">{d.label}</span>
                      <span className="font-semibold text-gray-900 ml-1">{d.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Price alerts */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Volatile material price alerts
              </div>
              <span className="text-xs text-amber-600 font-medium">{volatileItems.length} volatile items tracked</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {volatileItems.slice(0, 4).map(item => (
                <div key={item.id} className={`rounded-xl border px-4 py-3 flex items-center justify-between gap-3 ${
                  item.priceDirection === 'UP' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                }`}>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-gray-900 truncate">{item.description}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{item.volatilityNote?.split('.')[0]}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-bold text-gray-900">${item.currentPriceExGST.toFixed(2)}<span className="text-xs text-gray-400">/{item.unit}</span></div>
                    <TrendBadge pct={item.priceChangePct} invertColor />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── CATEGORY TAB ── */}
      {tab === 'category' && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-semibold text-gray-900">Spend by procurement category</div>
              <div className="text-xs text-gray-500 mt-0.5">YTD ex GST · vs prior quarter · includes labour from timesheets (CM-11)</div>
            </div>
            <div className="text-xs text-gray-500">Total: {fmtAUD(totalYTD)}</div>
          </div>
          {/* CM-11: Labour row from timesheet aggregation */}
          {(() => {
            const labourTotal = computeLabourCostFromTimesheets()
            const totalWithLabour = totalYTD + labourTotal
            const labourPct = labourTotal / totalWithLabour * 100
            return (
              <div className="flex items-center gap-3 py-1.5 mb-2 bg-indigo-50 rounded-xl px-3 -mx-1">
                <div className="w-36 flex-shrink-0">
                  <div className="text-xs font-semibold text-indigo-900 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-indigo-500 inline-block"/>
                    Labour (timesheets)
                  </div>
                  <div className="text-[10px] text-indigo-600">{MOCK_TIMESHEETS.length} timesheet records</div>
                </div>
                <div className="flex-1 h-7 bg-indigo-100 rounded-lg overflow-hidden">
                  <div className="h-full rounded-lg flex items-center px-2"
                    style={{ width:`${Math.max(labourPct * 3, 2)}%`, background:'#6366f1', minWidth:60 }}>
                    <span className="text-[10px] font-bold text-white">{fmtAUD(labourTotal)}</span>
                  </div>
                </div>
                <div className="w-12 text-right text-xs font-semibold text-indigo-700">{labourPct.toFixed(1)}%</div>
                <div className="w-16 text-right"><span className="text-[10px] text-indigo-600 font-medium">Timesheets</span></div>
              </div>
            )
          })()}
          {CATEGORY_SPEND.map(cat => (
            <div key={cat.category} className="flex items-center gap-3 py-1.5">
              <div className="w-36 flex-shrink-0">
                <div className="text-xs font-medium text-gray-800">{cat.label}</div>
                <div className="text-[10px] text-gray-400">{cat.invoiceCount} invoice{cat.invoiceCount !== 1 ? 's' : ''}</div>
              </div>
              <div className="flex-1 h-7 bg-gray-100 rounded-lg overflow-hidden">
                <div className="h-full rounded-lg flex items-center px-2"
                  style={{ width:`${cat.pctOfTotal === 0 ? 1 : cat.pctOfTotal * (100 / 33)}%`, background:cat.color, minWidth:36 }}>
                  <span className="text-[10px] font-bold text-white">{fmtAUD(cat.totalExGST)}</span>
                </div>
              </div>
              <div className="w-12 text-right text-xs font-semibold text-gray-600">{cat.pctOfTotal.toFixed(1)}%</div>
              <div className="w-16 text-right">
                <TrendBadge pct={cat.trendPct} invertColor />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── SUPPLIERS TAB ── */}
      {tab === 'suppliers' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b bg-gray-50">
            <div className="text-sm font-semibold text-gray-900">Spend by supplier</div>
            <div className="text-xs text-gray-500 mt-0.5">YTD totals · ranked by spend · ex GST</div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 bg-gray-50 border-b">
                <th className="text-left px-5 py-3 font-medium">Supplier</th>
                <th className="text-left px-3 py-3 font-medium">Category</th>
                <th className="text-right px-3 py-3 font-medium">YTD spend</th>
                <th className="text-right px-3 py-3 font-medium">vs last year</th>
                <th className="text-right px-3 py-3 font-medium">Invoices</th>
                <th className="text-right px-3 py-3 font-medium">Avg pay (days)</th>
                <th className="text-left px-5 py-3 font-medium">Bar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {SUPPLIER_SPEND.map((sup, i) => (
                <tr key={sup.supplierId} className="hover:bg-gray-50">
                  <td className="px-5 py-3">
                    <div className="font-medium text-gray-900">{sup.supplierName}</div>
                    <div className="text-xs text-gray-400 font-mono">ABN {sup.abn}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600">
                    {sup.primaryCategory.replace(/_/g,' ').toLowerCase().replace(/\b\w/g,c=>c.toUpperCase())}
                    {sup.hasContract && <span className="ml-1.5 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Contract</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-semibold text-gray-900">{fmtAUD(sup.totalExGST)}</td>
                  <td className="px-3 py-3 text-right">
                    <TrendBadge pct={sup.ytdChangeVsLastYear} invertColor />
                  </td>
                  <td className="px-3 py-3 text-right text-gray-600">{sup.invoiceCount}</td>
                  <td className={`px-3 py-3 text-right font-medium ${sup.avgPaymentDays > 35 ? 'text-red-600' : sup.avgPaymentDays > 25 ? 'text-amber-600' : 'text-green-700'}`}>
                    {sup.avgPaymentDays}d
                  </td>
                  <td className="px-5 py-3 w-48">
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-[#2E75B6]"
                        style={{ width:`${(sup.totalExGST / MAX_SUPPLIER)*100}%`, opacity:1 - (i*0.08) }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t">
              <tr>
                <td colSpan={2} className="px-5 py-3 text-xs text-gray-500">8 suppliers · {SUPPLIER_SPEND.reduce((s,v)=>s+v.invoiceCount,0)} invoices total</td>
                <td className="px-3 py-3 text-right text-sm font-bold text-gray-900">{fmtAUD(SUPPLIER_SPEND.reduce((s,v)=>s+v.totalExGST,0))}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ── JOBS TAB ── */}
      {tab === 'jobs' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-sm font-semibold text-gray-900 mb-1">Budget utilisation by job</div>
            <div className="text-xs text-gray-500 mb-4">Committed PO value as % of contract · warns at 80% · blocks at 100%</div>
            <div className="divide-y divide-gray-50">
              {JOB_SPEND.map(j => <JobUtilRow key={j.jobId} j={j} />)}
            </div>
          </div>

          {/* Job spend detail table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b bg-gray-50 text-sm font-semibold text-gray-900">Job spend breakdown</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 bg-gray-50 border-b">
                  <th className="text-left px-5 py-3 font-medium">Job</th>
                  <th className="text-right px-3 py-3 font-medium">Contract value</th>
                  <th className="text-right px-3 py-3 font-medium">Committed (POs)</th>
                  <th className="text-right px-3 py-3 font-medium">Invoiced</th>
                  <th className="text-right px-3 py-3 font-medium">Paid</th>
                  <th className="text-right px-5 py-3 font-medium">% committed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {JOB_SPEND.map((j, i) => (
                  <tr key={j.jobId} className={`hover:bg-gray-50 ${i%2===0?'':'bg-gray-50/30'}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-900">{j.jobNumber}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">{j.jobName}</div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-gray-700">{fmtAUD(j.contractValue)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-[#2E75B6] font-medium">{fmtAUD(j.totalCommitted)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-amber-600 font-medium">{fmtAUD(j.totalInvoiced)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-green-700 font-medium">{fmtAUD(j.totalPaid)}</td>
                    <td className={`px-5 py-3 text-right font-bold tabular-nums ${j.budgetUtilisationPct>=80?'text-amber-600':j.budgetUtilisationPct>=95?'text-red-600':'text-gray-700'}`}>
                      {j.budgetUtilisationPct}%
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t">
                <tr>
                  <td className="px-5 py-3 text-xs text-gray-500 font-medium">Portfolio totals</td>
                  <td className="px-3 py-3 text-right font-bold text-gray-900">{fmtAUD(JOB_SPEND.reduce((s,j)=>s+j.contractValue,0))}</td>
                  <td className="px-3 py-3 text-right font-bold text-[#2E75B6]">{fmtAUD(JOB_SPEND.reduce((s,j)=>s+j.totalCommitted,0))}</td>
                  <td className="px-3 py-3 text-right font-bold text-amber-600">{fmtAUD(JOB_SPEND.reduce((s,j)=>s+j.totalInvoiced,0))}</td>
                  <td className="px-3 py-3 text-right font-bold text-green-700">{fmtAUD(JOB_SPEND.reduce((s,j)=>s+j.totalPaid,0))}</td>
                  <td className="px-5 py-3 text-right font-bold text-gray-900">
                    {Math.round(JOB_SPEND.reduce((s,j)=>s+j.totalCommitted,0)/JOB_SPEND.reduce((s,j)=>s+j.contractValue,0)*100)}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
